import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Button, Chip, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, IconButton, Divider,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import {
  getQboStatus, getQboAuthUrl, disconnectQbo,
  getQboCustomers, syncAllQboCustomers, qboFlagOff,
} from '../../Redux/Actions/qboAction';

export default function QboSettings() {
  const dispatch = useDispatch();
  const {
    loading, connected, companyName, lastSyncAt,
    customers, qboCustomersMeta, syncResult, syncSuccess, error,
  } = useSelector((state) => state.Qbo);

  const [showCustomers, setShowCustomers] = useState(false);

  useEffect(() => {
    dispatch(getQboStatus());
    // URL에서 QBO 콜백 결과 확인
    const params = new URLSearchParams(window.location.search);
    if (params.get('qbo') === 'connected') {
      dispatch(getQboStatus());
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [dispatch]);

  useEffect(() => {
    if (syncSuccess) {
      const timer = setTimeout(() => dispatch(qboFlagOff()), 3000);
      return () => clearTimeout(timer);
    }
  }, [syncSuccess, dispatch]);

  const handleConnect = () => dispatch(getQboAuthUrl());
  const handleDisconnect = () => {
    if (window.confirm('QuickBooks 연결을 해제하시겠습니까?')) {
      dispatch(disconnectQbo());
    }
  };
  const handleLoadCustomers = () => {
    setShowCustomers(true);
    dispatch(getQboCustomers({ all: true }));
  };
  const handleSyncAll = () => {
    if (window.confirm('모든 QuickBooks 고객 데이터를 CMS Store와 동기화하시겠습니까?')) {
      dispatch(syncAllQboCustomers());
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          QuickBooks Online 연동
        </Typography>
        {connected ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="연결됨"
            color="success"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<WarningIcon />}
            label="연결 안 됨"
            color="default"
            variant="outlined"
          />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {syncSuccess && syncResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          동기화 완료: {syncResult.synced || 0}건 매핑 업데이트
          {syncResult.created > 0 && `, ${syncResult.created}건 신규 Store 생성`}
          {syncResult.totalFromQbo != null && ` (QBO 활성 고객 ${syncResult.totalFromQbo}명`}
          {syncResult.pagesFetched != null && `, API ${syncResult.pagesFetched}페이지`}
          {syncResult.totalFromQbo != null && ')'}
          {syncResult.errors?.length > 0 && ` · 오류 ${syncResult.errors.length}건`}
        </Alert>
      )}

      {connected ? (
        <>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              회사: <strong>{companyName || '-'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              마지막 동기화: {lastSyncAt ? new Date(lastSyncAt).toLocaleString('ko-KR') : '없음'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={handleSyncAll}
              disabled={loading}
              size="small"
            >
              {loading ? <CircularProgress size={20} /> : '전체 동기화'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleLoadCustomers}
              disabled={loading}
              size="small"
            >
              고객 전체 불러오기
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={handleDisconnect}
              size="small"
            >
              연결 해제
            </Button>
          </Box>

          {showCustomers && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                QuickBooks 활성 고객 ({customers.length}명)
                {qboCustomersMeta?.pagesFetched != null && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    · QBO 쿼리 {qboCustomersMeta.pagesFetched}페이지
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                전 페이지를 순회해 가져옵니다. 고객 수가 많으면 수십 초 걸릴 수 있습니다.
              </Typography>
              {loading && !customers.length ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>이름</TableCell>
                        <TableCell>주소</TableCell>
                        <TableCell>전화</TableCell>
                        <TableCell>이메일</TableCell>
                        <TableCell>CMS 매핑</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customers.map((c, i) => (
                        <TableRow key={c.qboRaw?.Id || c.qboCustomerId || `${c.name}-${i}`} hover>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>
                            {[c.address1, c.town, c.state, c.zipcode].filter(Boolean).join(', ')}
                          </TableCell>
                          <TableCell>{c.storePhone}</TableCell>
                          <TableCell>{c.ownerEmail}</TableCell>
                          <TableCell>
                            {c.isMapped ? (
                              <Chip label="매핑됨" size="small" color="success" variant="outlined" />
                            ) : (
                              <Chip label="미매핑" size="small" color="default" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {customers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">고객 데이터가 없습니다.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            QuickBooks Online을 연결하면 고객 주소, 인보이스 등의 데이터를 자동으로 가져올 수 있습니다.
          </Typography>
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            onClick={handleConnect}
            disabled={loading}
            sx={{ bgcolor: '#2CA01C', '&:hover': { bgcolor: '#238c16' } }}
          >
            {loading ? <CircularProgress size={20} /> : 'QuickBooks 연결하기'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
